import styled from '@emotion/styled';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  i18n,
  openCallMeModal,
  setCustomer,
  setExamDraft,
  setFlowType,
  setGlobalLoading,
  setProgressBarPercentage,
  setSoId,
  setSoIdWeb,
  SoHealthAdditionalInfo,
  useAppDispatch,
} from 'src/common';
import { useConfirmRenewalSoDataMutation, useGetSoQuery } from 'src/common/api/apollo/additional';
import {
  ArrowRightIcon,
  Button,
  Card,
  emptyHealthExams,
  Flex,
  HealthExamsForm,
  InputPhoneNumber,
  Loader,
  RequestAgentCardV2,
  Text,
  validateHealthExams,
} from 'src/components';
import { colors } from 'src/theme';
import { AppRoutesAdditional } from '../../_router/app.routes';

const RowStyle = styled(Flex)({
  justifyContent: 'space-between',
  gap: 16,
});

const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD.MM.YYYY.') : '');

/** GraphQL `additionalInsuranceInfo` → the exam form's value (no `__typename`). */
const toExams = (info: Record<string, unknown> | null | undefined): SoHealthAdditionalInfo => {
  if (!info) return emptyHealthExams;
  const rest = Object.fromEntries(Object.entries(info).filter(([key]) => key !== '__typename'));
  return { ...emptyHealthExams, ...(rest as Partial<SoHealthAdditionalInfo>) };
};

/**
 * Selfcare renewal entry: `obnova?soId=<encrypted>` (health-api CreateRenewalSo
 * link). The SO already exists with last year's data, so steps 1-3 are
 * replaced by this confirmation. A renewal is for the same person: name, birth
 * date and e-mail are shown read-only (a different person or e-mail is a new
 * SO through the regular funnel — the button below the data leads there);
 * only the phone and the exam wishes can be corrected. Insured person,
 * contractor and delivery are confirmed later, on the offer's data
 * confirmation, as in the regular funnel.
 */
const RenewalPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const soId = searchParams.get('soId') ?? '';

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [exams, setExams] = useState<SoHealthAdditionalInfo>(emptyHealthExams);
  const [examsError, setExamsError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [invalidLink, setInvalidLink] = useState(!soId);
  // Inputs read their initial value on mount, so the form renders only once the SO data is in state.
  const [prefilled, setPrefilled] = useState(false);

  const [confirmRenewalSoData] = useConfirmRenewalSoDataMutation();

  useEffect(() => {
    dispatch(setFlowType('web'));
    dispatch(setProgressBarPercentage(30));
    if (soId) {
      dispatch(setSoIdWeb(soId));
      dispatch(setSoId(soId));
    }
  }, [soId]);

  const { data, loading } = useGetSoQuery({
    variables: { id: soId },
    skip: !soId,
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const so = result.getSo;
      const renewalOf = so?.productTypeSpecific?.renewalOf;
      if (!so || renewalOf?.productTypeCode !== 'dodatno') {
        setInvalidLink(true);
        return;
      }
      const insured = so.productTypeSpecific?.insuredPerson;
      const birthDate = insured?.birthDate ? dayjs(insured.birthDate).format('YYYY-MM-DD') : '';
      setPhone(so.phoneNumber ?? '');
      setExams(toExams(so.productTypeSpecific?.additionalInsuranceInfo as Record<string, unknown> | null | undefined));
      // Contact name is the contractor's (SO `data`), not the insured person's.
      dispatch(setCustomer({
        birthDate,
        age: birthDate ? dayjs().diff(dayjs(birthDate), 'year') : undefined,
        firstName: so.firstName ?? '',
        lastName: so.lastName ?? '',
        email: insured?.partnerContactEmail || so.email || '',
        phoneNumber: so.phoneNumber ?? '',
        hasPolicy: true,
      }));
      setPrefilled(true);
    },
    onError: () => setInvalidLink(true),
  });

  const so = data?.getSo;
  const renewalOf = so?.productTypeSpecific?.renewalOf;
  const insured = so?.productTypeSpecific?.insuredPerson;

  const handleSubmit = async () => {
    if (!soId) return;
    let valid = true;
    if (!phone.trim()) {
      setPhoneError(i18n.t('pages.customerData.validation.phoneRequired'));
      valid = false;
    }
    const examsValidation = validateHealthExams(exams);
    setExamsError(examsValidation);
    if (examsValidation) valid = false;
    if (!valid) return;

    setGeneralError('');
    dispatch(setGlobalLoading(true));
    try {
      await confirmRenewalSoData({ variables: { id: soId, input: { phoneNumber: phone.trim(), additionalInsuranceInfo: exams } } });
      dispatch(setCustomer({ phoneNumber: phone.trim() }));
      dispatch(setExamDraft(exams));
      navigate(`${AppRoutesAdditional.OFFERS_PREVIEW.fullPath}/${soId}`);
    } catch (error) {
      const invalidPhone = error instanceof Error && error.message.includes('Invalid phone');
      if (invalidPhone) {
        setPhoneError(i18n.t('pages.customerData.validation.phoneRequired'));
      } else {
        setGeneralError(i18n.t('pages.customerData.validation.error'));
      }
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  if (loading || (!invalidLink && !prefilled)) return <Loader page />;

  if (invalidLink || !so || !renewalOf) {
    return (
      <Card radius={12} padding={20}>
        <Flex vertical gap={32} justify="center" align="center">
          <Text type="base" color={colors.text2}>{i18n.t('pages.renewal.invalidLink')}</Text>
          <Button text={i18n.t('pages.renewal.newCalculation')} onClick={() => navigate(AppRoutesAdditional.CUSTOMER_ONLY_DATE.fullPath)} />
          <RequestAgentCardV2 onButtonClick={() => dispatch(openCallMeModal())} />
        </Flex>
      </Card>
    );
  }

  const personRows: Array<[string, string]> = [
    [i18n.t('pages.renewal.name'), `${so.firstName ?? ''} ${so.lastName ?? ''}`.trim()],
    [i18n.t('pages.renewal.birthDate'), formatDate(insured?.birthDate)],
    [i18n.t('pages.renewal.email'), insured?.partnerContactEmail || so.email || ''],
  ];

  return (
    <Flex vertical align="center" gap={32}>
      <Card>
        <Flex vertical gap={32}>
          <Flex vertical gap={8}>
            <Text type="large" bold>{i18n.t('pages.renewal.title')}</Text>
            <Text type="small" color={colors.text3}>{i18n.t('pages.renewal.description')}</Text>
            {renewalOf.oldPolicy?.endDate && (
              <Text type="small" color={colors.text3}>
                {i18n.t('pages.renewal.currentPolicy', {
                  brandName: renewalOf.oldPolicy.brandName ?? renewalOf.oldPolicy.brandCode ?? '',
                  endDate: formatDate(renewalOf.oldPolicy.endDate),
                  startDate: formatDate(renewalOf.newPolicyStartDate),
                })}
              </Text>
            )}
          </Flex>

          <Flex vertical gap={12}>
            <Text type="medium">{i18n.t('pages.renewal.person')}</Text>
            {personRows.map(([label, value]) => (
              <RowStyle key={label}>
                <Text type="small" color={colors.text3}>{label}</Text>
                <Text type="small" bold>{value}</Text>
              </RowStyle>
            ))}
            <Text type="tiny" color={colors.text3}>{i18n.t('pages.renewal.changeDataHint')}</Text>
            <Button
              btnType="textPrimary"
              text={i18n.t('pages.renewal.changeDataButton')}
              onClick={() => navigate(AppRoutesAdditional.CUSTOMER_ONLY_DATE.fullPath)}
            />
          </Flex>

          <InputPhoneNumber
            id="renewalPhone"
            label={i18n.t('pages.renewal.phone')}
            value={phone}
            error={phoneError}
            onChange={(value) => {
              setPhone(value.phoneNumber);
              if (phoneError) setPhoneError('');
            }}
          />

          <Flex vertical gap={16}>
            <Text type="medium">{i18n.t('pages.renewal.exams')}</Text>
            <HealthExamsForm
              value={exams}
              onChange={(value) => {
                setExams(value);
                if (examsError) setExamsError('');
              }}
            />
            {examsError && <Text type="small" color={colors.error1}>{examsError}</Text>}
          </Flex>

          <Flex vertical gap={8}>
            <Button onClick={handleSubmit} btnType="primary" size="large" text={i18n.t('pages.renewal.submit')} className="btnBlock">
              <ArrowRightIcon color={colors.white} />
            </Button>
            {generalError && (
              <Text type="small" color={colors.error1} style={{ textAlign: 'center' }}>
                {generalError}
              </Text>
            )}
          </Flex>
        </Flex>
      </Card>
      <RequestAgentCardV2 onButtonClick={() => dispatch(openCallMeModal())} />
    </Flex>
  );
};

export default RenewalPage;
